import { Check } from "lucide-react";
import { PetQuiz } from "@/components/PetQuiz";

function Text({ section }) {
  return (
    <div className="max-w-3xl">
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-4">{section.heading}</h2>
      )}
      {section.body && (
        <p className="text-base leading-relaxed text-charcoal-600 whitespace-pre-line">{section.body}</p>
      )}
    </div>
  );
}

function Checklist({ section }) {
  return (
    <div className="max-w-3xl">
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-6">{section.heading}</h2>
      )}
      <ul className="space-y-3">
        {(section.items || []).map((item, i) => (
          <li key={i} className="flex items-start gap-3 rounded-2xl bg-white p-4 border border-sand-200">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage-light text-sage-700">
              <Check size={14} strokeWidth={2} />
            </span>
            <span className="text-base text-charcoal-700">{typeof item === "string" ? item : item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Steps({ section }) {
  return (
    <div className="max-w-3xl">
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">{section.heading}</h2>
      )}
      <div className="relative pl-8">
        <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-sage/40" />
        <div className="space-y-8">
          {(section.items || []).map((item, i) => (
            <div key={i} className="relative">
              <span className="absolute -left-8 top-1.5 h-4 w-4 rounded-full bg-terracotta ring-4 ring-sand-100" />
              {typeof item === "string" ? (
                <p className="text-base leading-relaxed text-charcoal-700">{item}</p>
              ) : (
                <>
                  <h3 className="text-lg font-medium text-charcoal-800">{item.title}</h3>
                  <p className="mt-1 text-base leading-relaxed text-charcoal-600">{item.text}</p>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Cards({ section }) {
  return (
    <div>
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">{section.heading}</h2>
      )}
      <div className="grid gap-6 md:grid-cols-3">
        {(section.items || []).map((item, i) => (
          <div
            key={i}
            className="rounded-3xl bg-white p-8 border border-sand-200 shadow-[0_8px_32px_rgba(44,42,41,0.06)] transition-transform hover:-translate-y-1"
          >
            <h3 className="text-xl font-medium text-charcoal-800 mb-2">{item.title}</h3>
            <p className="text-base leading-relaxed text-charcoal-600">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Gallery({ section }) {
  return (
    <div>
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-8">{section.heading}</h2>
      )}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(section.items || []).map((item, i) => (
          <figure key={i} className="overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-[0_8px_32px_rgba(44,42,41,0.06)]">
            <img src={item.image} alt={item.caption || section.heading || "Guide photo"} loading="lazy"
              className="aspect-[3/2] w-full object-cover transition-transform duration-500 hover:scale-105" />
            {item.caption && (
              <figcaption className="px-5 py-4 text-sm leading-relaxed text-charcoal-600">{item.caption}</figcaption>
            )}
          </figure>
        ))}
      </div>
    </div>
  );
}

function PhotoDiary({ section }) {
  return (
    <div data-testid="photo-diary">
      {section.heading && (
        <h2 className="text-2xl sm:text-3xl font-normal text-charcoal-800 mb-3">{section.heading}</h2>
      )}
      {section.body && (
        <p className="max-w-3xl text-base leading-relaxed text-charcoal-600 mb-8">{section.body}</p>
      )}
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {(section.items || []).map((item, i) => (
          <figure key={i} data-testid={`photo-diary-item-${i}`}
            className="overflow-hidden rounded-3xl border border-sand-200 bg-white shadow-[0_8px_32px_rgba(44,42,41,0.06)] transition-transform hover:-translate-y-1">
            <img src={item.image} alt={item.title} loading="lazy" className="aspect-[3/2] w-full object-cover" />
            <figcaption className="px-5 py-4">
              <p className="text-xs uppercase tracking-wider text-terracotta font-medium">{item.title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-charcoal-600">{item.text}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

export default function SectionRenderer({ section }) {
  const map = { text: Text, checklist: Checklist, steps: Steps, cards: Cards, gallery: Gallery, pet_quiz: PetQuiz, photo_diary: PhotoDiary };
  const Component = map[section.type] || Text;
  return <Component section={section} />;
}
